// Where a private note goes when it is promoted to a draft of a pull
// request (docs/design.md "Notes", Promotion). A draft is published as a
// review comment on the right side of the pull request's diff, so a note
// becomes one on the lines its own lines are at in the new side's blob of
// the comparison shown: the same lines when the note is on that blob, else
// the lines the line map from its blob carries them to (`EngineApi.lineMap`,
// kept by review/lineMaps.ts), spanning those that land. Lines that take a
// line comment (one run of the diff's commentable lines) make a `line`
// draft with the note's text; others make a `file` draft whose text names
// and quotes the lines (logic/offdiff, as the composer writes a comment
// outside the diff). A note none of whose lines land is not promoted, and
// says why. Nothing here reads a layout or a store: the same note, file
// and line maps give the same plan.
import type { Note, PromotionTarget } from '../api/server';
import { lineMapKey } from '../review/model';
import { buildOffDiffBody } from './offdiff';
import type { Hunk } from './suggestEdit';

/** The file of the comparison a note is promoted on, as its new side shows it. */
export interface PromotionFile {
  /** The file's path at the new side (a renamed file's new name). */
  path: string;
  /** Its blob at the new side: null for a file the comparison deletes. */
  blob: string | null;
  /** Other IDs of that side's text (`DiffModel.aliasOf`: its decoded text's `blobId` where it differs from GitHub's SHA): a note on one is on the blob. */
  aliases?: readonly string[];
  /** Its new side's lines (quoted by a file draft). */
  lines: readonly string[];
  /** The runs of new-side lines that take a line comment (the diff's hunks); none where no line does. */
  hunks: readonly Hunk[];
  /** The commit the new side is read at: the draft's. */
  commit: string;
  /** The pull request changes the file (a rebase's noise is not its file: GitHub takes no review comment on it). */
  inPr: boolean;
}

/** Where a pull request's comments are made: its repository, and GitHub's web address (a file draft links its lines). */
export interface PromotionPlace {
  owner: string;
  repo: string;
  webUrl: string;
}

/** What promoting a note on a file comes to. */
export type PromotionPlan =
  /** A draft at `target` with `body`, on new-side lines `from..to`. */
  | { kind: 'ready'; target: PromotionTarget; body: string; from: number; to: number }
  /** The line map from the note's blob to the file's is not computed yet. */
  | { kind: 'waiting'; need: { from: string; to: string } }
  /** It cannot be promoted here, and `why`. */
  | { kind: 'refused'; why: string };

/** Lines `a..b`, said as a sentence says them. */
export const linesText = (a: number, b: number) => (a === b ? `line ${a}` : `lines ${a}–${b}`);

/** Whether `from..to` lies within one run of `hunks`. */
function inOneHunk(hunks: readonly Hunk[], from: number, to: number): boolean {
  return hunks.some((h) => h.start <= from && to <= h.end);
}

/** How `note` is promoted on `file`, with the line maps computed so far. */
export function promotionPlan(note: Note, file: PromotionFile, lineMaps: ReadonlyMap<string, readonly number[]>, place: PromotionPlace): PromotionPlan {
  if (!file.inPr) return { kind: 'refused', why: 'The pull request doesn’t change this file, so it takes no review comment on it.' };
  if (file.blob === null) return { kind: 'refused', why: 'The pull request deletes this file: its lines aren’t there to comment on.' };
  let from = note.start_line;
  let to = note.end_line;
  if (note.blob !== file.blob && !file.aliases?.includes(note.blob)) {
    const key = lineMapKey(note.blob, file.blob);
    const map = lineMaps.get(key);
    if (!map) return { kind: 'waiting', need: { from: note.blob, to: file.blob } };
    const landed: number[] = [];
    for (let l = note.start_line; l <= note.end_line; l++) {
      const at = map[l - 1] ?? 0;
      if (at) landed.push(at);
    }
    if (!landed.length)
      return {
        kind: 'refused',
        why: `${note.end_line > note.start_line ? 'None of its lines are' : 'Its line isn’t'} in this version of the file (at ${file.commit.slice(0, 7)}), so it can’t become a comment here.`,
      };
    from = Math.min(...landed);
    to = Math.max(...landed);
  }
  if (inOneHunk(file.hunks, from, to))
    return {
      kind: 'ready',
      target: { kind: 'line', path: file.path, side: 'RIGHT', line: to, start_line: from < to ? from : null, commit_sha: file.commit },
      body: note.body,
      from,
      to,
    };
  const body = buildOffDiffBody({
    marker: { v: 1, path: file.path, line: to, side: 'RIGHT', commit: file.commit, ...(from < to ? { start: from } : {}) },
    lines: file.lines.slice(from - 1, to),
    text: note.body,
    ...place,
  });
  return { kind: 'ready', target: { kind: 'file', path: file.path, commit_sha: file.commit }, body, from, to };
}

/** The line maps `plans` wait on, each once. */
export function waitingOn(plans: Iterable<PromotionPlan>): { from: string; to: string }[] {
  const out = new Map<string, { from: string; to: string }>();
  for (const p of plans) if (p.kind === 'waiting') out.set(lineMapKey(p.need.from, p.need.to), p.need);
  return [...out.values()];
}
